import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Font
} from '@react-pdf/renderer';
// @ts-ignore
const logoCentralis = require('../../assets/logo/logo-centralis-gris.png');
// @ts-ignore
const bandeauCentralis = require('../../assets/logo/bandeau-centralis.png');

// Définition des interfaces TypeScript
interface Article {
  id: number;
  id_section: number;
  description: string | null;
  unite: string;
  nom: string;
  code: number;
  prix: string | null;
  created_at: string;
  updated_at: string;
}

interface Section {
  id: number;
  name: string;
  id_operation: number;
  articles: Article[];
}

interface NoteTechnique {
  id: number;
  note_operationnel: number;
  commentaire_operationnel: string;
  description_operationnel: string;
  ponderation_operationnel: number;
  note_humain: number;
  commentaire_humain: string;
  description_humain: string;
  ponderation_humain: number;
  note_environnement: number;
  commentaire_environnement: string;
  description_environnement: string;
  ponderation_environnement: number;
  note_technique: number;
  created_at: string;
  updated_at: string;
}

interface Prestataire {
  id: number;
  id_lot_departement: number;
  id_contrat: number;
  id_user: number | null;
  type_compte: string | null;
  id_entite: number;
  id_note_technique: number;
  id_status: number;
  nom: string | null;
  delais: number | null;
  adresse: string | null;
  cordonnee: string | null;
  modalite: string | null;
  visite: boolean | null;
  commentaire_visite: string | null;
  note_financiere: number | null;
  note_total: number | null;
  prix_total: number | null;
  expire_at: string;
  created_at: string;
  updated_at: string;
  id_operation: number;
  sections: Section[];
  note_technique: NoteTechnique;
  nom_entite: string;
  reponse?: string;
}

interface LotTechnique {
  id: number;
  nom: string;
}

interface Commande {
  id: number;
  organisme_id: number;
  id_user: number;
  id_lot_technique: number;
  id_lot_departement: number;
  type_compte: string;
  adresse: string;
  besoin: string | null;
  cordonnee: string | null;
  modalite: string | null;
  visite: boolean;
  commentaire: string;
  numero: string;
  nom: string;
  prix_total: number | null;
  id_status: number;
  reference: string | null;
  delais: number;
  echeance: string | null;
  code_postal: string;
  ville: string;
  coordonnees_maitrise: string | null;
  coordonnees_maitrise_ouvrage: string | null;
  date_debut_operation: string | null;
  date_fin_operation: string | null;
  expire_at: string;
  created_at: string;
  updated_at: string;
  id_commande: number | null;
  sections: Section[];
  status: {
    id: number;
    action: boolean;
    nom: string;
    delai: number;
    type_compte: string;
  };
  commandes: Prestataire[];
  organisme_nom: string;
  lot_technique: LotTechnique;
  reponse?: string;
}

interface User {
  id?: number;
  nom?: string;
  email?: string;
}

interface AnalysePrestatairePDFProps {
  commande: Commande;
  user: User;
  techNotes: Record<string, any>;
  calculateFinancialScore: (prestataires: Prestataire[]) => { [id: string]: number };
  getNoteGlobale: (techNotes: any, prestaId: string) => string;
  prestaId: number;
}

const styles = StyleSheet.create({
  page: {
    padding: 0,
    fontSize: 10, // Réduit de 12 à 10
    fontFamily: 'Helvetica',
    backgroundColor: '#FFFDFC'
  },
  bandeau: {
    width: '100%'
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20, // Réduit de 24 à 20
    paddingBottom: 12 // Réduit de 16 à 12
  },
  logo: {
    width: 80, // Réduit de 100 à 80
    marginRight: 12 // Réduit de 16 à 12
  },
  userInfo: {
    fontSize: 12, // Réduit de 14 à 12
    fontWeight: 'bold',
    textAlign: 'right'
  },
  dateInfo: {
    fontSize: 10, // Réduit de 12 à 10
    color: '#6B7280',
    textAlign: 'right',
    marginTop: 3 // Réduit de 4 à 3
  },
  infoSection: {
    padding: 12, // Réduit de 16 à 12
    marginHorizontal: 20, // Réduit de 24 à 20
    marginBottom: 12, // Réduit de 16 à 12
    backgroundColor: '#F9FAFB',
    borderRadius: 6, // Réduit de 8 à 6
    borderWidth: 1,
    borderColor: '#E5E7EB'
  },
  infoRow: {
    flexDirection: 'row',
    marginBottom: 6 // Réduit de 8 à 6
  },
  infoLabel: {
    fontWeight: 'bold',
    width: '40%',
    fontSize: 9 // Réduit de 11 à 9
  },
  infoValue: {
    flex: 1,
    fontSize: 9 // Réduit de 11 à 9
  },
  sectionTitle: {
    fontSize: 14, // Réduit de 16 à 14
    fontWeight: 'bold',
    backgroundColor: 'rgba(215, 217, 217, 0.25)',
    padding: 10, // Réduit de 12 à 10
    marginHorizontal: 20, // Réduit de 24 à 20
    marginTop: 14, // Réduit de 16 à 14
    marginBottom: 8,
    borderRadius: 6 // Réduit de 8 à 6
  },
  sectionContent: {
    marginHorizontal: 20, // Réduit de 24 à 20
    marginBottom: 14, // Réduit de 16 à 14
    padding: 10, // Réduit de 12 à 10
    backgroundColor: '#FFFDFC',
    borderRadius: 6, // Réduit de 8 à 6
    borderWidth: 1,
    borderColor: '#E5E7EB'
  },
  fieldLabel: {
    fontSize: 10, // Réduit de 12 à 10
    fontWeight: 'bold',
    marginBottom: 4 // Réduit de 6 à 4
  },
  fieldValue: {
    fontSize: 9, // Réduit de 11 à 9
    marginBottom: 12, // Réduit de 16 à 12
    lineHeight: 1.3 // Réduit de 1.4 à 1.3
  },
  prestaHeader: {
    marginBottom: 12, // Réduit de 16 à 12
    padding: 10, // Réduit de 12 à 10
    backgroundColor: 'rgba(215, 217, 217, 0.20)',
    borderRadius: 6
  },
  prestaName: {
    fontWeight: 'bold',
    fontSize: 14, // Réduit de 16 à 14
    marginBottom: 3 // Réduit de 4 à 3
  },
  prestaDate: {
    fontSize: 9, // Réduit de 11 à 9
    color: '#6B7280'
  },
  subsectionTitle: {
    fontSize: 12, // Réduit de 14 à 12
    fontWeight: 'bold',
    marginTop: 14, // Réduit de 16 à 14
    marginBottom: 6, // Réduit de 8 à 6
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    paddingBottom: 3 // Réduit de 4 à 3
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    paddingVertical: 6 // Réduit de 8 à 6
  },
  tableHeaderRow: {
    backgroundColor: 'rgba(215, 217, 217, 0.25)',
    fontWeight: 'bold'
  },
  tableCell: {
    flex: 1,
    padding: 5, // Réduit de 6 à 5
    fontSize: 9 // Réduit de 11 à 9
  },
  tableArticleCell: {
    width: 50, // Réduit de 60 à 50
    padding: 5, // Réduit de 6 à 5
    fontSize: 9 // Réduit de 11 à 9
  },
  tablePriceCell: {
    width: 80, // Réduit de 100 à 80
    padding: 5, // Réduit de 6 à 5
    fontSize: 9, // Réduit de 11 à 9
    textAlign: 'right'
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingTop: 10, // Réduit de 12 à 10
    marginTop: 6, // Réduit de 8 à 6
    fontWeight: 'bold',
    fontSize: 10 // Réduit de 12 à 10
  },
  notesGrid: {
    marginTop: 12, // Réduit de 16 à 12
    marginBottom: 12, // Réduit de 16 à 12
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 6,
    padding: 10, // Réduit de 12 à 10
    backgroundColor: '#F9FAFB'
  },
  noteRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8, // Réduit de 10 à 8
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    paddingBottom: 6 // Réduit de 8 à 6
  },
  noteLabel: {
    fontSize: 10, // Réduit de 12 à 10
    fontWeight: 'bold',
    width: '60%'
  },
  noteValue: {
    fontSize: 10, // Réduit de 12 à 10
    fontWeight: 'bold',
    width: '40%',
    textAlign: 'right'
  },
  commentLabel: {
    fontSize: 10, // Réduit de 12 à 10
    fontWeight: 'bold',
    marginBottom: 4 // Réduit de 6 à 4
  },
  commentBox: {
    padding: 6, // Réduit de 8 à 6
    backgroundColor: '#F9FAFB',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 4,
    marginTop: 3, // Réduit de 4 à 3
    marginBottom: 12 // Réduit de 16 à 12
  },
  commentText: {
    fontSize: 9, // Réduit de 11 à 9
    lineHeight: 1.3 // Réduit de 1.4 à 1.3
  },
  footer: {
    position: 'absolute',
    bottom: 20, // Réduit de 30 à 20
    left: 20, // Réduit de 24 à 20
    right: 20, // Réduit de 24 à 20
    fontSize: 8, // Réduit de 10 à 8
    color: '#6B7280',
    textAlign: 'center',
    paddingTop: 6, // Réduit de 8 à 6
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB'
  },
  // Nouveaux styles pour éviter les coupures de page
  pageBreakAvoid: {
    breakInside: 'avoid'
  },
  sectionContainer: {
    marginBottom: 10,
    breakInside: 'avoid'
  },
  // Styles pour les sections de page individuelle
  pageSectionContent: {
    marginHorizontal: 20,
    marginBottom: 14,
    padding: 10,
    backgroundColor: '#FFFDFC',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    minHeight: '60%', // Pour que le contenu prenne de la place même s'il est petit
  },
  // Réduction supplémentaire de taille pour les sections à fort contenu
  smallerText: {
    fontSize: 8,
    lineHeight: 1.2
  },
  // Style pour les grands tableaux
  compactTableCell: {
    flex: 1,
    padding: 3,
    fontSize: 8
  },
  compactTableArticleCell: {
    width: 40,
    padding: 3,
    fontSize: 8
  },
  compactTablePriceCell: {
    width: 70,
    padding: 3,
    fontSize: 8,
    textAlign: 'right'
  },
  compactTableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    paddingVertical: 4
  }
});

/**
 * Composant d'en-tête du document PDF
 */
const PDFHeader: React.FC<{ user: User; today: string }> = ({ user, today }) => (
  <View style={[styles.header, styles.pageBreakAvoid]}>
    <View>
      <Text style={styles.userInfo}>{user?.nom || 'Utilisateur'}</Text>
      <Text style={styles.dateInfo}>Document généré le {today}</Text>
    </View>
  </View>
);

/**
 * Composant d'informations générales sur la consultation
 */
const ConsultationInfo: React.FC<{ commande: Commande }> = ({ commande }) => (
  <View style={[styles.infoSection, styles.pageBreakAvoid]}>
    <View style={styles.infoRow}>
      <Text style={styles.infoLabel}>{commande.nom || 'Consultation'}</Text>
    </View>
    <View style={styles.infoRow}>
      <Text style={styles.infoLabel}>
        N° {commande.numero || `MS-${commande.id}`} • {commande.type_compte || 'MS'}
      </Text>
    </View>
    <View style={styles.infoRow}>
      <Text style={styles.infoLabel}>Lot technique:</Text>
      <Text style={styles.infoValue}>
        {commande.lot_technique?.nom || '-'}
      </Text>
    </View>
    <View style={styles.infoRow}>
      <Text style={styles.infoLabel}>Lot géographique:</Text>
      <Text style={styles.infoValue}>
        {commande.id_lot_departement || '-'}
      </Text>
    </View>
  </View>
);

/**
 * Composant qui affiche les articles du prestataire
 */
const ArticlesTable: React.FC<{ articles: Array<{ id: number; numero: string; prestation: string; prix: number }> }> = ({ articles }) => {
  const montantTotal = articles.reduce((sum, art) => sum + art.prix, 0);

  return (
    <View style={styles.pageBreakAvoid}>
      <View style={[styles.tableRow, styles.tableHeaderRow]}>
        <Text style={styles.tableArticleCell}>N°</Text>
        <Text style={styles.tableCell}>Prestation</Text>
        <Text style={styles.tablePriceCell}>Prix HT</Text>
      </View>

      {articles.map((article) => (
        <View key={article.id} style={styles.tableRow}>
          <Text style={styles.tableArticleCell}>{article.numero}</Text>
          <Text style={styles.tableCell}>{article.prestation}</Text>
          <Text style={styles.tablePriceCell}>
            {article.prix ? article.prix.toLocaleString('fr-FR') : "0"} €
          </Text>
        </View>
      ))}

      <View style={styles.totalRow}>
        <Text>Total HT: {montantTotal.toLocaleString('fr-FR')} €</Text>
      </View>
    </View>
  );
};

/**
 * Version compacte du tableau d'articles pour les grands volumes
 */
const CompactArticlesTable: React.FC<{ articles: Array<{ id: number; numero: string; prestation: string; prix: number }> }> = ({ articles }) => {
  const montantTotal = articles.reduce((sum, art) => sum + art.prix, 0);
  const normalizePdfText = (value: string): string => {
    return value.replace(/[\u202F\u00A0]/g, ' ');
  };

  const totalHT = normalizePdfText(
    montantTotal.toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
  return (
    <View>
      <View style={[styles.compactTableRow, styles.tableHeaderRow]}>
        <Text style={styles.compactTableArticleCell}>N°</Text>
        <Text style={styles.compactTableCell}>Prestation</Text>
        <Text style={styles.compactTablePriceCell}>Prix HT</Text>
      </View>

      {articles.map((article) => (
        <View key={article.id} style={styles.compactTableRow}>
          <Text style={styles.compactTableArticleCell}>{article.numero}</Text>
          <Text style={styles.compactTableCell}>{article.prestation}</Text>
          <Text style={styles.compactTablePriceCell}>
            {article.prix ? normalizePdfText(article.prix.toLocaleString("fr-FR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })) : "0"} €
          </Text>
        </View>
      ))}

      <View style={styles.totalRow}>
        <Text>Total HT: {totalHT} €</Text>
      </View>
    </View>
  );
};

/**
 * Composant d'analyse technique
 */
const TechnicalAnalysis: React.FC<{ prestataire: Prestataire; techNotes: any; prestaIdStr: string }> = ({
  prestataire,
  techNotes,
  prestaIdStr
}) => (
  <View style={styles.sectionContainer}>
    <Text style={styles.subsectionTitle}>Analyse technique</Text>

    <View style={styles.pageBreakAvoid}>
      <Text style={styles.commentLabel}>Organisation - Modes Opératoires pour l'exécution du marché</Text>
      <View style={styles.commentBox}>
        <Text style={styles.commentText}>
          {prestataire.note_technique?.commentaire_operationnel ||
            techNotes[prestaIdStr]?.organisation?.commentaire ||
            "Aucun commentaire"}
        </Text>
      </View>
    </View>

    <View style={styles.pageBreakAvoid}>
      <Text style={styles.commentLabel}>Moyens mis en œuvre</Text>
      <View style={styles.commentBox}>
        <Text style={styles.commentText}>
          {prestataire.note_technique?.commentaire_humain ||
            techNotes[prestaIdStr]?.moyens?.commentaire ||
            "Aucun commentaire"}
        </Text>
      </View>
    </View>

    <View style={styles.pageBreakAvoid}>
      <Text style={styles.commentLabel}>Critères sociaux et environnementaux</Text>
      <View style={styles.commentBox}>
        <Text style={styles.commentText}>
          {prestataire.note_technique?.commentaire_environnement ||
            techNotes[prestaIdStr]?.social?.commentaire ||
            "Aucun commentaire"}
        </Text>
      </View>
    </View>
  </View>
);

/**
 * Composant d'affichage des notes
 */
const TechnicalScores: React.FC<{ prestataire: Prestataire; techNotes: any; prestaIdStr: string; techScore: number }> = ({
  prestataire,
  techNotes,
  prestaIdStr,
  techScore
}) => (
  <View style={[styles.notesGrid, styles.pageBreakAvoid]}>
    <View style={styles.noteRow}>
      <Text style={styles.noteLabel}>Organisation</Text>
      <Text style={styles.noteValue}>
        {prestataire.note_technique?.note_operationnel.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ||
          techNotes[prestaIdStr]?.organisation?.note.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ||
          "0"}/5
      </Text>
    </View>
    <View style={styles.noteRow}>
      <Text style={styles.noteLabel}>Moyens</Text>
      <Text style={styles.noteValue}>
        {prestataire.note_technique?.note_humain.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ||
          techNotes[prestaIdStr]?.moyens?.note.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ||
          "0"}/4
      </Text>
    </View>
    <View style={styles.noteRow}>
      <Text style={styles.noteLabel}>Social</Text>
      <Text style={styles.noteValue}>
        {prestataire.note_technique?.note_environnement.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ||
          techNotes[prestaIdStr]?.social?.note.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ||
          "0"}/1
      </Text>
    </View>
    <View style={styles.noteRow}>
      <Text style={styles.noteLabel}>NOTE TECHNIQUE GLOBALE</Text>
      <Text style={styles.noteValue}>{techScore.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/10</Text>
    </View>
  </View>
);


/**
 * Composant principal du PDF d'analyse de prestataire
 */
const AnalysePrestatairePDF: React.FC<AnalysePrestatairePDFProps> = ({
  commande,
  user,
  techNotes,
  calculateFinancialScore,
  getNoteGlobale,
  prestaId
}) => {
  const today = new Date().toLocaleDateString('fr-FR');

  // Trouver le prestataire correspondant dans la liste des prestataires
  const prestataire = commande.commandes?.find(cmd => cmd.id === prestaId) ||
    (Array.isArray(commande.commandes) && prestaId < commande.commandes.length ?
      commande.commandes[prestaId] : null);

  // Vérifier si le prestataire existe
  if (!prestataire) {
    console.error('Prestataire non trouvé avec ID:', prestaId);
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <Text>Prestataire non trouvé (ID: {prestaId})</Text>
        </Page>
      </Document>
    );
  }

  // Utiliser l'ID du prestataire pour les notes techniques
  const prestaIdStr = prestaId.toString();

  // Calculer les notes
  const financialScores = calculateFinancialScore(commande.commandes || []);

  // Récupération des notes techniques
  let techScore = 0;
  if (techNotes[prestaIdStr]) {
    techScore = parseFloat(getNoteGlobale(techNotes, prestaIdStr));
  } else if (prestataire.note_technique) {
    techScore = (
      (prestataire.note_technique.note_operationnel || 0) +
      (prestataire.note_technique.note_humain || 0) +
      (prestataire.note_technique.note_environnement || 0)
    );
  }

  // Note financière
  const finScore = financialScores[prestaIdStr] !== undefined ? financialScores[prestaIdStr] :
    prestataire.note_financiere !== null ? prestataire.note_financiere : 0;

  // Note totale
  const totalScore = (techScore * 0.55) + (finScore * 0.45);

  // Extraire les articles des sections
  const articles = prestataire.sections?.reduce<Array<{ id: number; numero: string; prestation: string; prix: number }>>(
    (acc, section) => {
      if (section && section.articles && Array.isArray(section.articles)) {
        const sectionArticles = section.articles.map(article => ({
          id: article.id,
          numero: article.code?.toString() || "-",
          prestation: article.nom || "Sans nom",
          prix: article.prix ? parseFloat(article.prix) : 0
        }));
        return [...acc, ...sectionArticles];
      }
      return acc;
    },
    []
  ) || [];

  // Diviser les articles en groupes de taille raisonnable
  const maxArticlesPerPage = 20; // Augmenté car on utilise une version plus compacte
  const articleGroups = [];

  for (let i = 0; i < articles.length; i += maxArticlesPerPage) {
    articleGroups.push(articles.slice(i, i + maxArticlesPerPage));
  }

  // Créer un footer standard
  const Footer = ({ pageNumber, totalPages }: { pageNumber: number, totalPages: number }) => (
    <Text style={styles.footer}>
      Document généré par Centralis • {today} • {user?.email || ''} • Page {pageNumber}/{totalPages}
    </Text>
  );

  return (
    <Document>
      {/* Page 1: En-tête et informations générales */}
      <Page size="A4" style={styles.page}>
        {/* Bandeau Centralis */}
        <Image src={bandeauCentralis} style={styles.bandeau} />

        {/* En-tête avec informations utilisateur et date */}
        <PDFHeader user={user} today={today} />

        {/* Informations générales */}
        <ConsultationInfo commande={commande} />

        {/* Informations Prestataire */}
        <Text style={styles.sectionTitle}>
          Analyse de l'offre - {prestataire.nom_entite || prestataire.nom || `Prestataire ${prestaId}`}
        </Text>

        <View style={styles.pageSectionContent}>
          <View style={styles.prestaHeader}>
            <Text style={styles.prestaName}>
              {prestataire.nom_entite || prestataire.nom || `Prestataire ${prestaId}`}
            </Text>
            <Text style={styles.prestaDate}>
              Offre reçue le {prestataire.created_at ? new Date(prestataire.created_at).toLocaleDateString('fr-FR') : today}
            </Text>
          </View>

          {/* Offre Technique - version courte */}
          <Text style={styles.subsectionTitle}>Réponse technique</Text>
          <View style={styles.fieldValue}>
            <Text style={styles.smallerText}>
              {(prestataire.reponse && prestataire.reponse.length > 300)
                ? prestataire.reponse.substring(0, 300) + "..."
                : prestataire.reponse || "Aucune réponse technique fournie."}
            </Text>
            {prestataire.reponse && prestataire.reponse.length > 300 && (
              <Text style={{ fontSize: 8, fontStyle: 'italic' }}>
                (La réponse complète est disponible sur la page suivante)
              </Text>
            )}
          </View>
        </View>

        <Footer pageNumber={1} totalPages={2 + Math.ceil(articles.length / maxArticlesPerPage)} />
      </Page>

      {/* Page 2: Réponse technique complète si nécessaire */}
      {prestataire.reponse && prestataire.reponse.length > 300 && (
        <Page size="A4" style={styles.page}>
          <PDFHeader user={user} today={today} />

          <Text style={styles.sectionTitle}>
            Réponse technique complète
          </Text>

          <View style={styles.pageSectionContent}>
            <Text style={styles.smallerText}>
              {prestataire.reponse}
            </Text>
          </View>

          <Footer pageNumber={2} totalPages={3 + Math.ceil(articles.length / maxArticlesPerPage)} />
        </Page>
      )}

      {/* Page 3: Analyse technique */}
      <Page size="A4" style={styles.page}>
        <PDFHeader user={user} today={today} />

        <Text style={styles.sectionTitle}>
          Analyse technique
        </Text>

        <View style={styles.pageSectionContent}>
          <Text style={styles.commentLabel}>Organisation - Modes Opératoires pour l'exécution du marché</Text>
          <View style={styles.commentBox}>
            <Text style={styles.smallerText}>
              {prestataire.note_technique?.commentaire_operationnel ||
                techNotes[prestaIdStr]?.organisation?.commentaire ||
                "Aucun commentaire"}
            </Text>
          </View>

          <Text style={styles.commentLabel}>Moyens mis en œuvre</Text>
          <View style={styles.commentBox}>
            <Text style={styles.smallerText}>
              {prestataire.note_technique?.commentaire_humain ||
                techNotes[prestaIdStr]?.moyens?.commentaire ||
                "Aucun commentaire"}
            </Text>
          </View>

          <Text style={styles.commentLabel}>Critères sociaux et environnementaux</Text>
          <View style={styles.commentBox}>
            <Text style={styles.smallerText}>
              {prestataire.note_technique?.commentaire_environnement ||
                techNotes[prestaIdStr]?.social?.commentaire ||
                "Aucun commentaire"}
            </Text>
          </View>

          <View style={styles.notesGrid}>
            <View style={styles.noteRow}>
              <Text style={styles.noteLabel}>Organisation</Text>
              <Text style={styles.noteValue}>
                {prestataire.note_technique?.note_operationnel.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ||
                  techNotes[prestaIdStr]?.organisation?.note.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ||
                  "0"}/5
              </Text>
            </View>
            <View style={styles.noteRow}>
              <Text style={styles.noteLabel}>Moyens</Text>
              <Text style={styles.noteValue}>
                {prestataire.note_technique?.note_humain.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ||
                  techNotes[prestaIdStr]?.moyens?.note.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ||
                  "0"}/4
              </Text>
            </View>
            <View style={styles.noteRow}>
              <Text style={styles.noteLabel}>Social</Text>
              <Text style={styles.noteValue}>
                {prestataire.note_technique?.note_environnement.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ||
                  techNotes[prestaIdStr]?.social?.note.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ||
                  "0"}/1
              </Text>
            </View>
            <View style={styles.noteRow}>
              <Text style={styles.noteLabel}>NOTE TECHNIQUE GLOBALE</Text>
              <Text style={styles.noteValue}>{techScore.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/10</Text>
            </View>
          </View>
        </View>

        <Footer pageNumber={prestataire.reponse && prestataire.reponse.length > 300 ? 3 : 2}
          totalPages={prestataire.reponse && prestataire.reponse.length > 300 ?
            3 + Math.ceil(articles.length / maxArticlesPerPage) :
            2 + Math.ceil(articles.length / maxArticlesPerPage)} />
      </Page>

      {/* Page 4: Analyse financière */}
      <Page size="A4" style={styles.page}>
        <PDFHeader user={user} today={today} />

        <Text style={styles.sectionTitle}>
          Analyse financière
        </Text>

        <View style={styles.pageSectionContent}>
          {articles.length <= maxArticlesPerPage ? (
            <CompactArticlesTable articles={articles} />
          ) : (
            <Text style={styles.commentText}>
              Les articles sont présentés sur les pages suivantes en raison de leur nombre.
            </Text>
          )}

          <View style={styles.notesGrid}>
            <View style={styles.noteRow}>
              <Text style={styles.noteLabel}>NOTE FINANCIÈRE</Text>
              <Text style={styles.noteValue}>{finScore.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/10</Text>
            </View>
          </View>

          {/* Synthèse */}
          <Text style={styles.subsectionTitle}>Synthèse de l'analyse</Text>

          <View style={styles.notesGrid}>
            <View style={styles.noteRow}>
              <Text style={styles.noteLabel}>Note technique (55%)</Text>
              <Text style={styles.noteValue}>{techScore.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/10</Text>
            </View>
            <View style={styles.noteRow}>
              <Text style={styles.noteLabel}>Note financière (45%)</Text>
              <Text style={styles.noteValue}>{finScore.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/10</Text>
            </View>
            <View style={styles.noteRow}>
              <Text style={styles.noteLabel}>NOTE GLOBALE</Text>
              <Text style={styles.noteValue}>{totalScore.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/10</Text>
            </View>
          </View>
        </View>

        <Footer
          pageNumber={prestataire.reponse && prestataire.reponse.length > 300 ? 4 : 3}
          totalPages={prestataire.reponse && prestataire.reponse.length > 300 ?
            4 + articleGroups.length - (articles.length <= maxArticlesPerPage ? 1 : 0) :
            3 + articleGroups.length - (articles.length <= maxArticlesPerPage ? 1 : 0)}
        />
      </Page>

      {/* Pages supplémentaires pour les articles si nécessaire */}
      {articles.length > maxArticlesPerPage && articleGroups.map((group, index) => (
        <Page key={`articles-page-${index}`} size="A4" style={styles.page}>
          <PDFHeader user={user} today={today} />

          <Text style={styles.sectionTitle}>
            Articles - Page {index + 1}
          </Text>

          <View style={styles.pageSectionContent}>
            <CompactArticlesTable articles={group} />
          </View>

          <Footer
            pageNumber={index + (prestataire.reponse && prestataire.reponse.length > 300 ? 5 : 4)}
            totalPages={prestataire.reponse && prestataire.reponse.length > 300 ?
              4 + articleGroups.length :
              3 + articleGroups.length}
          />
        </Page>
      ))}
    </Document>
  );
};

export default AnalysePrestatairePDF;